// src/pages/merchant/Wallet.js
// Portefeuille marchand : solde, gains, retraits
// Maxula

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiDollarSign, FiClock, FiArrowUpRight, FiArrowDownLeft, FiAlertTriangle, FiSend, FiX, FiXCircle, FiCreditCard, FiFileText, FiCalendar, FiMapPin } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { walletAPI } from '../../services/api';

const fmt = (v) => (Number(v) || 0).toLocaleString('fr-FR');

const Card = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-5">
    <div className="flex items-center gap-2 text-gray-500 text-sm mb-1"><Icon size={16} style={{ color }} /> {label}</div>
    <div className="text-2xl font-bold text-gray-900">{fmt(value)} <span className="text-sm font-normal text-gray-400">MAD</span></div>
  </div>
);

const statusBadge = (s) => {
  const map = { completed: 'bg-green-100 text-green-700', pending: 'bg-yellow-100 text-yellow-700', rejected: 'bg-red-100 text-red-700', cancelled: 'bg-gray-100 text-gray-600' };
  // « Payé » et non « Validé » : c'est le vocabulaire demandé (En attente / Payé)
  // et le seul qui soit sans ambiguïté pour le partenaire.
  const label = s === 'completed' ? 'Payé' : s === 'pending' ? 'En attente' : s === 'rejected' ? 'Refusé' : s === 'cancelled' ? 'Annulé' : s;
  return <span className={`text-xs px-2 py-0.5 rounded-full ${map[s] || 'bg-gray-100 text-gray-600'}`}>{label}</span>;
};

const txLabel = (t) => (t.type === 'withdrawal' ? 'Retrait' : t.type === 'credit' ? 'Crédit' : t.type === 'debit' ? 'Débit' : t.type);

// v1.419.0 — modes de règlement
const METHOD_ICONS = { bank_transfer: FiCreditCard, cash: FiDollarSign, cheque: FiFileText };

// Date seule, sans heure : une date de paiement est une date civile. Afficher
// une heure laisserait croire à un créneau de rendez-vous.
const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(String(d).length <= 10 ? `${d}T12:00:00Z` : d);
  return Number.isNaN(dt.getTime()) ? '—' : dt.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
};

const MerchantWallet = () => {
  const [wallet, setWallet] = useState(null);
  const [txs, setTxs] = useState([]);
  const [earnings, setEarnings] = useState(null); // détail brut/commissions/remises/net
  // P2-D : exports comptables
  const nowM = new Date();
  const defMonth = `${nowM.getFullYear()}-${String(nowM.getMonth() + 1).padStart(2, '0')}`;
  const [expMonth, setExpMonth] = useState(defMonth);
  const [expYear, setExpYear] = useState(nowM.getFullYear());
  const [expBusy, setExpBusy] = useState(null);
  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; document.body.appendChild(a);
    a.click(); a.remove(); window.URL.revokeObjectURL(url);
  };
  const doExport = async (kind, format) => {
    const key = `${kind}-${format}`;
    setExpBusy(key);
    try {
      const r = kind === 'earnings'
        ? await walletAPI.exportEarnings(expMonth, format)
        : await walletAPI.exportWithdrawals(expYear, format);
      const ext = format === 'pdf' ? 'pdf' : 'csv';
      const name = kind === 'earnings'
        ? `maxula-revenus-${expMonth}.${ext}`
        : `maxula-retraits-${expYear}.${ext}`;
      downloadBlob(new Blob([r.data]), name);
    } catch (e) {
      toast.error('Erreur lors de la génération du fichier');
    } finally { setExpBusy(null); }
  };
  const [hasBankInfo, setHasBankInfo] = useState(true);
  const [bankInfo, setBankInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  // v1.419.0 — mode de règlement et date prévue
  const [method, setMethod] = useState('bank_transfer');
  const [payout, setPayout] = useState(null);   // { methods, businessDays, preview, pickup }
  const [note, setNote] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      const res = await walletAPI.getMine();
      const d = res.data?.data || {};
      setWallet(d.wallet || null);
      walletAPI.getEarnings().then((r) => setEarnings(r?.data?.data || null)).catch(() => {});
      setTxs(d.transactions || []);
      setHasBankInfo(d.hasBankInfo !== false);
      setBankInfo(d.bankInfo || null);
      // Les options de paiement sont chargées à part : si cette route n'est
      // pas encore déployée, le portefeuille reste utilisable (repli virement).
      walletAPI.payoutOptions()
        .then((r) => {
          const po = r?.data?.data || null;
          setPayout(po);
          // On présélectionne le premier mode réellement utilisable : proposer
          // « virement » par défaut à un partenaire sans RIB le mènerait droit
          // à un refus.
          const first = (po?.methods || []).find((m) => m.available);
          if (first) setMethod(first.key);
        })
        .catch(() => setPayout(null));
    } catch (e) { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const [confirmTx, setConfirmTx] = useState(null);   // retrait à annuler (modal)
  const [cancelBusy, setCancelBusy] = useState(false);

  const cancelWithdraw = (t) => setConfirmTx(t);

  const doCancelWithdraw = async () => {
    if (!confirmTx) return;
    setCancelBusy(true);
    try {
      await walletAPI.cancelWithdraw(confirmTx.id || confirmTx._id);
      toast.success('Demande annulée. Le montant est de nouveau disponible.');
      setConfirmTx(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Erreur lors de l'annulation");
    } finally {
      setCancelBusy(false);
    }
  };

  const w = wallet || {};
  const available = Number(w.available) || 0;

  const withdraw = async () => {
    const a = Number(amount);
    if (!a || a <= 0) return toast.error('Montant invalide');
    if (a > available) return toast.error('Montant supérieur au solde disponible');
    try {
      setBusy(true);
      const res = await walletAPI.withdraw(a, method, note);
      const due = res?.data?.payout?.scheduledDate;
      toast.success(due
        ? `Demande enregistrée. Règlement disponible le ${fmtDate(due)}.`
        : 'Demande de paiement envoyée');
      setAmount('');
      setNote('');
      load();
    } catch (e) { toast.error(e?.response?.data?.message || 'Erreur'); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="flex justify-center py-24"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" /></div>;

  // Le RIB n'est requis QUE pour le virement : un partenaire qui vient
  // chercher des espèces au guichet n'a aucune raison d'en fournir un.
  const methodList = payout?.methods || [{ key: 'bank_transfer', label: 'Virement bancaire / RIB', pickup: false, available: hasBankInfo }];
  const selected = methodList.find((m) => m.key === method) || methodList[0];
  const canWithdraw = available > 0 && !busy && !!selected && selected.available !== false;

  return (
    <div className="p-6 max-w-4xl">
      <h1 className="text-3xl font-bold text-gray-900 mb-2">Mon portefeuille</h1>
      <p className="text-gray-500 mb-6">Suivez vos gains et demandez votre règlement : virement bancaire, espèces ou chèque.</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card icon={FiArrowUpRight} label="Total encaissé" value={w.totalEarned} color="#16a34a" />
        <Card icon={FiDollarSign} label="Disponible" value={w.available} color="#2563eb" />
        <Card icon={FiClock} label="En attente" value={w.pending} color="#d97706" />
        <Card icon={FiArrowDownLeft} label="Déjà retiré" value={w.withdrawn} color="#7c3aed" />
      </div>

      {!hasBankInfo && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-start gap-3">
          <FiAlertTriangle className="text-amber-500 mt-0.5 flex-shrink-0" size={20} />
          <div className="text-sm text-amber-800">
            <div className="font-semibold mb-0.5">RIB manquant</div>
            {/* v1.419.0 — ce bandeau disait « avant de pouvoir demander un
                retrait », ce qui est désormais faux : sans RIB, on peut très
                bien demander un règlement en espèces ou par chèque. Le laisser
                tel quel aurait découragé les partenaires qui viennent au
                guichet, exactement le cas que ce lot vient débloquer. */}
            Renseignez votre RIB/IBAN dans <Link to="/merchant/settings" className="underline font-medium">Paramètres</Link> pour être payé par virement. Vous pouvez aussi demander votre règlement en <strong>espèces</strong> ou par <strong>chèque</strong>, à retirer chez Maxula.
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
        <h2 className="font-semibold text-gray-900 mb-1 flex items-center gap-2"><FiSend size={16} className="text-primary-600" /> Demander un paiement</h2>
        <p className="text-sm text-gray-500 mb-4">
          Choisissez votre mode de règlement. Solde disponible : <strong>{fmt(available)} MAD</strong>.
        </p>

        {/* Mode de règlement */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          {methodList.map((m) => {
            const Icon = METHOD_ICONS[m.key] || FiCreditCard;
            const active = method === m.key;
            const disabled = m.available === false;
            return (
              <button
                key={m.key}
                type="button"
                onClick={() => !disabled && setMethod(m.key)}
                disabled={disabled}
                className={`text-left rounded-xl border p-3 transition ${
                  active ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-200'
                  : disabled ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                  : 'border-gray-300 hover:border-primary-300'
                }`}
              >
                <div className="flex items-center gap-2 font-medium text-gray-900 text-sm">
                  <Icon size={16} className={active ? 'text-primary-600' : 'text-gray-400'} />
                  {m.label}
                </div>
                {m.pickup && !disabled && (
                  <div className="text-xs text-gray-500 mt-1">À retirer chez Maxula</div>
                )}
                {/* Motif affiché plutôt que case masquée : le partenaire doit
                    comprendre ce qui lui manque, pas chercher une option
                    disparue. */}
                {disabled && m.unavailableReason && (
                  <div className="text-xs text-amber-700 mt-1">{m.unavailableReason}</div>
                )}
              </button>
            );
          })}
        </div>

        {method === 'bank_transfer' && hasBankInfo && bankInfo?.rib && (
          <p className="text-xs text-gray-400 mb-3">Virement vers : {bankInfo.rib}{bankInfo.bankName ? ` · ${bankInfo.bankName}` : ''}</p>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <input
              type="number" min="0" max={available} value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Montant à percevoir"
              disabled={available <= 0}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 pr-16 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50 disabled:text-gray-400"
            />
            <button type="button" onClick={() => setAmount(String(available))} disabled={available <= 0}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-primary-600 font-medium hover:text-primary-700 disabled:text-gray-300">Tout</button>
          </div>
          <button onClick={withdraw} disabled={!canWithdraw}
            className="px-5 py-2.5 rounded-lg bg-primary-600 text-white font-semibold hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2">
            {busy ? 'Envoi...' : 'Demander le paiement'}
          </button>
        </div>

        <input
          type="text" value={note} onChange={(e) => setNote(e.target.value)} maxLength={180}
          placeholder="Note facultative (référence, précision…)"
          className="mt-3 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />

        {/* Date prévue annoncée AVANT de valider, pas découverte après. */}
        {payout?.preview?.scheduledDate && (
          <div className="mt-4 rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 flex items-start gap-2">
            <FiCalendar className="text-blue-500 mt-0.5 shrink-0" size={16} />
            <div className="text-sm text-blue-900">
              En demandant aujourd'hui, votre règlement sera disponible le{' '}
              <strong>{fmtDate(payout.preview.scheduledDate)}</strong>{' '}
              <span className="text-blue-700">
                ({payout.businessDays} jours ouvrés, hors week-ends
                {payout.preview.holidaysSkipped?.length ? ' et jours fériés' : ''}).
              </span>
              {selected?.pickup && (
                <div className="mt-1">
                  Vous pourrez le retirer chez Maxula à partir de cette date.
                </div>
              )}
            </div>
          </div>
        )}

        {/* Adresse du guichet, uniquement pour espèces et chèque. */}
        {selected?.pickup && payout?.pickup?.address && (
          <div className="mt-3 text-xs text-gray-600 flex items-start gap-2">
            <FiMapPin className="text-gray-400 mt-0.5 shrink-0" size={14} />
            <div>
              {payout.pickup.address}
              {payout.pickup.hours ? <div>{payout.pickup.hours}</div> : null}
              {payout.pickup.phone ? <div>{payout.pickup.phone}</div> : null}
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-5">
        {earnings && (
          <div className="mb-6">
            <h2 className="font-semibold text-gray-900 mb-4">Détail des revenus (12 derniers mois)</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
              <div className="rounded-xl border border-gray-200 p-3">
                <div className="text-xs text-gray-500">Revenus bruts</div>
                <div className="font-bold">{fmt(earnings.totals?.gross)} MAD</div>
              </div>
              <div className="rounded-xl border border-gray-200 p-3">
                <div className="text-xs text-gray-500">Commissions plateforme</div>
                <div className="font-bold text-amber-600">- {fmt(earnings.totals?.fees)} MAD</div>
              </div>
              <div className="rounded-xl border border-gray-200 p-3">
                <div className="text-xs text-gray-500">Remises absorbées</div>
                <div className="font-bold text-amber-600">- {fmt(earnings.totals?.promoAbsorbed)} MAD</div>
              </div>
              {/* v1.404.0 — « Net a percevoir (cumul) » affichait le NET GAGNE sans
                  deduire les retraits deja payes (5 130 alors que 4 930 avaient
                  ete regles). Le reste a percevoir vient du serveur
                  (pendingPayout = gains - retraits payes - retraits en cours),
                  le meme chiffre que la carte « Disponible ». */}
              <div className="rounded-xl border border-primary-200 bg-primary-50 p-3">
                <div className="text-xs text-primary-700">Reste à percevoir</div>
                <div className="font-bold text-primary-700">{fmt(earnings.pendingPayout ?? w.available)} MAD</div>
                <div className="text-[11px] text-primary-600 mt-0.5">
                  Détail ci-dessous
                </div>
              </div>
            </div>

            {/* v1.406.0 — D'OU VIENT LE RESTE : calcul complet, derniere demande
                de retrait (montant, date, paiement) et gains enregistres depuis
                cette demande, ligne par ligne. */}
            {(() => {
              const dr = earnings.detailReste;
              if (!dr) return null;
              const lw = dr.lastWithdrawal;
              return (
                <div className="rounded-xl border border-primary-200 mb-4 overflow-hidden">
                  <div className="bg-primary-50 px-4 py-3 flex flex-wrap items-center justify-between gap-2">
                    <div className="font-semibold text-primary-800 flex items-center gap-2">
                      <FiFileText size={15} /> Détail du reste à percevoir
                    </div>
                    <div className="text-lg font-bold text-primary-700">{fmt(dr.reste)} MAD</div>
                  </div>

                  <div className="px-4 py-3 text-sm space-y-1.5 border-b border-gray-100">
                    <div className="flex justify-between"><span className="text-gray-600">Total net gagné (depuis le début)</span><span className="font-medium">{fmt(w.totalEarned)} MAD</span></div>
                    <div className="flex justify-between"><span className="text-gray-600">Retraits déjà payés</span><span className="font-medium text-green-700">- {fmt(w.withdrawn)} MAD</span></div>
                    {Number(w.pending) > 0 && (
                      <div className="flex justify-between"><span className="text-gray-600">Retrait demandé, en cours de paiement</span><span className="font-medium text-amber-600">- {fmt(w.pending)} MAD</span></div>
                    )}
                    <div className="flex justify-between border-t border-gray-100 pt-1.5"><span className="font-semibold">Reste à percevoir</span><span className="font-bold text-primary-700">{fmt(dr.reste)} MAD</span></div>
                  </div>

                  {lw && (
                    <div className="px-4 py-3 text-sm border-b border-gray-100">
                      <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Dernière demande de retrait</div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                        <span className="font-bold">{fmt(lw.amount)} MAD</span>
                        <span className="text-gray-600">demandé le {fmtDate(lw.requestedAt)}</span>
                        {lw.reference && <span className="text-gray-400">{lw.reference}</span>}
                        {lw.paymentMethodLabel && <span className="text-gray-500">{lw.paymentMethodLabel}</span>}
                        {lw.status === 'completed' ? (
                          <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-semibold">
                            Payé{lw.paidAt ? ` le ${fmtDate(lw.paidAt)}` : ''}
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold">
                            En attente{lw.scheduledPaymentDate ? ` — prévu le ${fmtDate(lw.scheduledPaymentDate)}` : ''}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="px-4 py-3 text-sm">
                    <div className="text-xs font-semibold text-gray-500 uppercase mb-2">
                      {lw ? `Gains enregistrés depuis la demande du ${fmtDate(lw.requestedAt)}` : 'Gains enregistrés'}
                      {' '}— {dr.nbDepuis} ligne{dr.nbDepuis > 1 ? 's' : ''} · {fmt(dr.netDepuis)} MAD net
                    </div>
                    {dr.lignes && dr.lignes.length > 0 ? (
                      <div className="overflow-x-auto rounded-lg border border-gray-200">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 text-gray-600">
                            <tr>
                              <th className="text-left px-3 py-2 font-medium">Date</th>
                              <th className="text-left px-3 py-2 font-medium">Commande</th>
                              <th className="text-left px-3 py-2 font-medium">Offre</th>
                              <th className="text-right px-3 py-2 font-medium">Brut</th>
                              <th className="text-right px-3 py-2 font-medium">Net</th>
                            </tr>
                          </thead>
                          <tbody>
                            {dr.lignes.map((l) => (
                              <tr key={l.id} className="border-t border-gray-100">
                                <td className="px-3 py-2 whitespace-nowrap">{fmtDate(l.date)}</td>
                                <td className="px-3 py-2 whitespace-nowrap">{l.orderNumber ? `#${l.orderNumber}` : '—'}</td>
                                <td className="px-3 py-2">{l.title || '—'}</td>
                                <td className="px-3 py-2 text-right">{fmt(l.gross)}</td>
                                <td className="px-3 py-2 text-right font-semibold">{fmt(l.net)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-gray-400">Aucun nouveau gain depuis cette demande.</p>
                    )}
                    {dr.nbDepuis > (dr.lignes ? dr.lignes.length : 0) && (
                      <p className="text-xs text-gray-400 mt-1">Les {dr.lignes.length} plus récentes sont affichées. Le relevé mensuel (PDF/CSV) contient tout.</p>
                    )}
                    {Math.abs(Number(dr.reliquat) || 0) >= 0.01 && (
                      <p className="text-xs text-gray-600 mt-2">
                        {Number(dr.reliquat) > 0
                          ? `Dont ${fmt(dr.reliquat)} MAD gagnés avant la demande et non inclus dans le retrait.`
                          : `Ajustement de ${fmt(dr.reliquat)} MAD (correction ou avoir enregistré par Maxula).`}
                      </p>
                    )}
                  </div>
                </div>
              );
            })()}
            {Array.isArray(earnings.monthly) && earnings.monthly.length > 0 && (
              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">Mois</th>
                      <th className="text-right px-3 py-2 font-medium">Brut</th>
                      <th className="text-right px-3 py-2 font-medium">Commissions</th>
                      <th className="text-right px-3 py-2 font-medium">Remises</th>
                      <th className="text-right px-3 py-2 font-medium">Net</th>
                    </tr>
                  </thead>
                  <tbody>
                    {earnings.monthly.map((m) => (
                      <tr key={m.month} className="border-t border-gray-100">
                        <td className="px-3 py-2 font-medium">{m.month}</td>
                        <td className="px-3 py-2 text-right">{fmt(m.gross)}</td>
                        <td className="px-3 py-2 text-right text-amber-600">- {fmt(m.fees)}</td>
                        <td className="px-3 py-2 text-right text-amber-600">- {fmt(m.promoAbsorbed)}</td>
                        <td className="px-3 py-2 text-right font-semibold">{fmt(m.net)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* P2-D : exports comptables */}
        <div className="mb-6">
          <h2 className="font-semibold text-gray-900 mb-1">Exports comptables</h2>
          <p className="text-sm text-gray-500 mb-4">Relevés pour votre comptable : revenus mensuels détaillés et retraits annuels.</p>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="rounded-xl border border-gray-200 p-4">
              <div className="font-medium text-sm mb-2">Revenus du mois</div>
              <input type="month" value={expMonth} onChange={(e) => setExpMonth(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-primary-200" />
              <div className="flex gap-2">
                <button type="button" disabled={expBusy === 'earnings-csv'} onClick={() => doExport('earnings', 'csv')}
                  className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-sm font-semibold hover:bg-gray-50 disabled:opacity-50">
                  {expBusy === 'earnings-csv' ? 'Génération...' : 'Télécharger CSV'}
                </button>
                <button type="button" disabled={expBusy === 'earnings-pdf'} onClick={() => doExport('earnings', 'pdf')}
                  className="flex-1 px-3 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 disabled:opacity-50">
                  {expBusy === 'earnings-pdf' ? 'Génération...' : 'Télécharger PDF'}
                </button>
              </div>
            </div>
            <div className="rounded-xl border border-gray-200 p-4">
              <div className="font-medium text-sm mb-2">Retraits de l'année</div>
              <select value={expYear} onChange={(e) => setExpYear(Number(e.target.value))}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-primary-200">
                {Array.from({ length: 5 }, (_, i) => nowM.getFullYear() - i).map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
              <div className="flex gap-2">
                <button type="button" disabled={expBusy === 'withdrawals-csv'} onClick={() => doExport('withdrawals', 'csv')}
                  className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-sm font-semibold hover:bg-gray-50 disabled:opacity-50">
                  {expBusy === 'withdrawals-csv' ? 'Génération...' : 'Télécharger CSV'}
                </button>
                <button type="button" disabled={expBusy === 'withdrawals-pdf'} onClick={() => doExport('withdrawals', 'pdf')}
                  className="flex-1 px-3 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 disabled:opacity-50">
                  {expBusy === 'withdrawals-pdf' ? 'Génération...' : 'Télécharger PDF'}
                </button>
              </div>
            </div>
          </div>
        </div>

        <h2 className="font-semibold text-gray-900 mb-4">Historique</h2>
        {txs.length === 0 ? (
          <div className="text-center text-gray-400 py-6">Aucun mouvement.</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {txs.map((t) => {
              const isWd = t.type === 'withdrawal';
              const MIcon = METHOD_ICONS[t.paymentMethod] || FiCreditCard;
              return (
                <div key={t._id || t.id} className="py-3 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium text-gray-900 flex items-center gap-2">
                      {txLabel(t)}
                      {isWd && (
                        <span className="inline-flex items-center gap-1 text-xs text-gray-500 font-normal">
                          <MIcon size={12} /> {t.paymentMethodLabel || 'Virement bancaire / RIB'}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-400">
                      Demande : {new Date(t.createdAt).toLocaleDateString('fr-FR')}{t.reference ? ` · ${t.reference}` : ''}
                    </div>
                    {/* Les anciennes demandes n'ont pas de date prévue : on
                        n'affiche la ligne que si l'information existe, plutôt
                        qu'un « — » qui ferait croire à une donnée perdue. */}
                    {isWd && t.scheduledPaymentDate && (
                      <div className="text-xs text-blue-700 mt-0.5 flex items-center gap-1">
                        <FiCalendar size={11} />
                        Prévu le {fmtDate(t.scheduledPaymentDate)}
                        {t.isPickup && t.status === 'pending' ? ' — à retirer chez Maxula' : ''}
                      </div>
                    )}
                    {isWd && t.paidAt && (
                      <div className="text-xs text-green-700 mt-0.5">
                        Payé le {fmtDate(t.paidAt)}
                      </div>
                    )}
                    {isWd && t.paymentNote && (
                      <div className="text-xs text-gray-500 mt-0.5">Réf. : {t.paymentNote}</div>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-semibold">{fmt(t.amount)} MAD</div>
                    <div className="flex items-center justify-end gap-2">
                      {statusBadge(t.status)}
                      {isWd && t.status === 'pending' && (
                        <button type="button" onClick={() => cancelWithdraw(t)}
                          className="text-xs font-medium text-red-600 hover:underline">
                          Annuler
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal de confirmation — annulation de retrait */}
      {confirmTx && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => !cancelBusy && setConfirmTx(null)} />
          <div className="relative bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden animate-[fadeIn_.15s_ease-out]">
            <div className="p-6">
              <div className="flex items-start gap-4">
                <span className="w-12 h-12 rounded-full bg-red-50 text-red-600 flex items-center justify-center flex-shrink-0">
                  <FiXCircle size={24} />
                </span>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-900">Annuler ce retrait ?</h3>
                  <p className="mt-1 text-sm text-gray-600">
                    La demande <span className="font-mono font-medium">{confirmTx.reference}</span> de{' '}
                    <span className="font-bold text-gray-900">{fmt(confirmTx.amount)} MAD</span> sera annulée.
                    Le montant sera immédiatement de nouveau disponible dans votre portefeuille.
                  </p>
                </div>
                <button type="button" onClick={() => !cancelBusy && setConfirmTx(null)}
                  className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600">
                  <FiX size={18} />
                </button>
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 flex items-center justify-end gap-3">
              <button type="button" disabled={cancelBusy} onClick={() => setConfirmTx(null)}
                className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50">
                Garder la demande
              </button>
              <button type="button" disabled={cancelBusy} onClick={doCancelWithdraw}
                className="px-5 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50 flex items-center gap-2">
                <FiXCircle size={15} /> {cancelBusy ? 'Annulation...' : 'Oui, annuler le retrait'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MerchantWallet;
